import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Users, TrendingUp, CheckCheck, XCircle, Send } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { Button, DataTable, type DataTableColumn } from "@akuna/ui";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useCampaigns, useDispatchCampaign, type CampaignStatus } from "@/hooks/useCampaigns";
import { estimateCampaignCost } from "@/lib/meta/pricing";
import type { MetaTemplateCategory } from "@/lib/meta/pricing";
import { useWhatsAppMessageTemplates } from "@/hooks/useWhatsAppMessageTemplates";
import { useCampaignDetail, type CampaignRecipient } from "@/hooks/useCampaignDetail";
import type { AudienceFilter } from "@/lib/campaigns/audience-query";

function RecipientStatusBadge({ status }: { status: CampaignRecipient["status"] }) {
  const { t } = useTranslation("campaigns");
  const variantMap: Record<
    CampaignRecipient["status"],
    "default" | "secondary" | "destructive" | "outline"
  > = {
    read: "default",
    delivered: "secondary",
    sent: "secondary",
    pending: "outline",
    failed: "destructive",
    skipped: "outline",
  };
  const labelMap: Record<CampaignRecipient["status"], string> = {
    pending: t("recipientStatusPending"),
    sent: t("recipientStatusSent"),
    delivered: t("recipientStatusDelivered"),
    read: t("recipientStatusRead"),
    failed: t("recipientStatusFailed"),
    skipped: t("recipientStatusSkipped"),
  };
  return <Badge variant={variantMap[status]}>{labelMap[status]}</Badge>;
}

function CampaignStatusBadge({ status }: { status: CampaignStatus }) {
  const { t } = useTranslation("campaigns");
  const variantMap: Record<CampaignStatus, "default" | "secondary" | "destructive" | "outline"> = {
    completed: "default",
    scheduled: "secondary",
    dispatching: "secondary",
    draft: "outline",
    failed: "destructive",
    cancelled: "secondary",
  };
  const labelMap: Record<CampaignStatus, string> = {
    draft: t("statusDraft"),
    scheduled: t("statusScheduled"),
    dispatching: t("statusRunning"),
    completed: t("statusDone"),
    failed: t("statusFailed"),
    cancelled: t("statusPaused"),
  };
  return <Badge variant={variantMap[status]}>{labelMap[status]}</Badge>;
}

function pct(num: number): string {
  return `${Math.round(num * 100)}%`;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "BRL" }).format(value);
}

export function CampaignDetailPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { t } = useTranslation("campaigns");
  const { toast } = useToast();
  const { data: campaigns = [] } = useCampaigns();
  const { data: templates = [] } = useWhatsAppMessageTemplates();
  const { data, isLoading } = useCampaignDetail(campaignId);
  const dispatchMutation = useDispatchCampaign();
  const [dispatchOpen, setDispatchOpen] = useState(false);

  const campaign = campaigns.find((c) => c.id === campaignId);
  const template = templates.find((tpl) => tpl.id === campaign?.templateId);

  const metrics = data?.metrics;
  const recipients = data?.recipients ?? [];
  const attributions = data?.attributions ?? [];

  const estimatedCost =
    campaign && template
      ? estimateCampaignCost(template.category as MetaTemplateCategory, campaign.estimatedRecipients ?? 0)
      : null;
  const roi =
    metrics && estimatedCost && estimatedCost > 0
      ? (metrics.totalAttributedRevenue - estimatedCost) / estimatedCost
      : null;

  const segmentChips = useMemo(() => {
    const filter: AudienceFilter = campaign?.audienceFilter ?? {};
    const chips: string[] = [];

    if (filter.ownerIds?.length) chips.push(t("segmentOwners", { count: filter.ownerIds.length }));
    if (filter.abcCurves?.length) chips.push(t("segmentAbc", { values: filter.abcCurves.join(", ") }));
    if (filter.speciesIds?.length) chips.push(t("segmentSpecies", { count: filter.speciesIds.length }));
    if (filter.breedIds?.length) chips.push(t("segmentBreeds", { count: filter.breedIds.length }));
    if (filter.genderIds?.length) chips.push(t("segmentGenders", { count: filter.genderIds.length }));
    if (filter.sizeCategoryIds?.length) chips.push(t("segmentSizes", { count: filter.sizeCategoryIds.length }));
    if (filter.coatTypeIds?.length) chips.push(t("segmentCoatTypes", { count: filter.coatTypeIds.length }));
    if (filter.petStatus) chips.push(t(`segmentPetStatus.${filter.petStatus}`));
    if (filter.ageGroups?.length) {
      chips.push(t("segmentAgeGroups", { values: filter.ageGroups.map((ag) => t(`ageGroup.${ag}`)).join(", ") }));
    }
    if (filter.detailedAgeGroups?.length) {
      chips.push(t("segmentAgeGroups", { values: filter.detailedAgeGroups.map((ag) => t(`segmentDetailedAge.${ag}`)).join(", ") }));
    }
    if (filter.ageYearsMin !== undefined || filter.ageYearsMax !== undefined) {
      chips.push(t("segmentAgeRange", {
        min: filter.ageYearsMin ?? 0,
        max: filter.ageYearsMax ?? t("segmentNoMax"),
      }));
    }
    if (filter.daysSinceVisitMin !== undefined) {
      chips.push(t("segmentInactiveMin", { count: filter.daysSinceVisitMin }));
    }
    if (filter.daysSinceVisitMax !== undefined) {
      chips.push(t("segmentInactiveMax", { count: filter.daysSinceVisitMax }));
    }
    if (filter.hasWhatsApp) chips.push(t("segmentWhatsapp"));
    if (filter.relationshipStatuses?.length) {
      chips.push(t("segmentRelationship", { values: filter.relationshipStatuses.map((status) => t(`segmentRelationshipStatus.${status}`)).join(", ") }));
    }
    if (filter.minVisits !== undefined) chips.push(t("segmentMinVisits", { count: filter.minVisits }));
    if (filter.minAverageTicket !== undefined) chips.push(t("segmentMinTicket", { value: filter.minAverageTicket }));
    if (filter.sterilized !== undefined) chips.push(filter.sterilized ? t("segmentSterilized") : t("segmentNotSterilized"));
    if (filter.weightStatus) chips.push(t(`segmentWeightStatus.${filter.weightStatus}`));

    return chips;
  }, [campaign?.audienceFilter, t]);

  const dataTableLabels = {
    columnsButton: t("dataTable.columns"),
    columnsTitle: t("dataTable.columnsTitle"),
    reset: t("dataTable.reset"),
    densityComfortable: t("dataTable.densityComfortable"),
    densityCompact: t("dataTable.densityCompact"),
    empty: t("dataTable.empty"),
  };

  const paginationLabels = {
    previous: t("dataTable.previous"),
    next: t("dataTable.next"),
  };

  const recipientColumns: DataTableColumn<CampaignRecipient>[] = [
    {
      id: "owner",
      header: t("colOwner"),
      cell: (recipient) => (
        <span className="font-medium text-sm">
          {recipient.ownerName ?? recipient.ownerId.slice(0, 8)}
        </span>
      ),
      sortAccessor: (recipient) => recipient.ownerName ?? recipient.ownerId,
      width: 220,
    },
    {
      id: "phone",
      header: t("colPhone"),
      cell: (recipient) => <span className="text-sm font-mono">{recipient.phoneNumber ?? "—"}</span>,
      sortAccessor: (recipient) => recipient.phoneNumber ?? "",
      width: 170,
    },
    {
      id: "status",
      header: t("colStatus"),
      cell: (recipient) => (
        <div className="space-y-0.5">
          <RecipientStatusBadge status={recipient.status} />
          {recipient.skipReason && (
            <p className="text-xs text-muted-foreground">{recipient.skipReason}</p>
          )}
          {recipient.errorMessage && (
            <p className="text-xs text-destructive">{recipient.errorMessage}</p>
          )}
        </div>
      ),
      sortAccessor: (recipient) => recipient.status,
      width: 220,
    },
    {
      id: "sentAt",
      header: t("colSentAt"),
      cell: (recipient) => (
        <span className="text-xs text-muted-foreground">
          {recipient.sentAt ? format(new Date(recipient.sentAt), "dd/MM HH:mm") : "—"}
        </span>
      ),
      sortAccessor: (recipient) => recipient.sentAt ?? "",
      width: 140,
    },
    {
      id: "deliveredAt",
      header: t("colDeliveredAt"),
      cell: (recipient) => (
        <span className="text-xs text-muted-foreground">
          {recipient.deliveredAt ? format(new Date(recipient.deliveredAt), "dd/MM HH:mm") : "—"}
        </span>
      ),
      sortAccessor: (recipient) => recipient.deliveredAt ?? "",
      width: 150,
    },
    {
      id: "readAt",
      header: t("colReadAt"),
      cell: (recipient) => (
        <span className="text-xs text-muted-foreground">
          {recipient.readAt ? format(new Date(recipient.readAt), "dd/MM HH:mm") : "—"}
        </span>
      ),
      sortAccessor: (recipient) => recipient.readAt ?? "",
      width: 130,
    },
  ];

  async function handleDispatch() {
    if (!campaignId) return;
    try {
      await dispatchMutation.mutateAsync(campaignId);
      toast({ title: t("dispatchSuccess") });
      setDispatchOpen(false);
    } catch {
      toast({ title: t("dispatchError"), variant: "destructive" });
    }
  }

  const canDispatch = campaign && (campaign.status === "draft" || campaign.status === "scheduled");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="sm" asChild className="mt-0.5">
          <Link to="/reports/campaigns">
            <ArrowLeft className="h-4 w-4 mr-1" />
            {t("detailBack")}
          </Link>
        </Button>
      </div>

      {campaign && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-xl font-semibold">{campaign.name}</h2>
            <CampaignStatusBadge status={campaign.status} />
            {campaign.scheduledAt && (
              <span className="text-sm text-muted-foreground">
                {format(new Date(campaign.scheduledAt), "dd/MM/yyyy HH:mm")}
              </span>
            )}
          </div>
          {canDispatch && (
            <Button
              size="sm"
              className="gap-1"
              onClick={() => setDispatchOpen(true)}
              disabled={dispatchMutation.isPending}
            >
              <Send className="h-4 w-4" />
              {t("dispatchNow")}
            </Button>
          )}
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium">{t("metricsSent")}</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold tabular-nums">
              {metrics?.totalSent.toLocaleString() ?? (isLoading ? "…" : "0")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium">{t("metricsDelivered")}</CardTitle>
            <CheckCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold tabular-nums">
              {metrics?.totalDelivered.toLocaleString() ?? (isLoading ? "…" : "0")}
            </p>
            {metrics && metrics.totalSent > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {t("metricsDeliveryRate")}: {pct(metrics.deliveryRate)}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium">{t("metricsRead")}</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold tabular-nums">
              {metrics?.totalRead.toLocaleString() ?? (isLoading ? "…" : "0")}
            </p>
            {metrics && metrics.totalDelivered > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {t("metricsReadRate")}: {pct(metrics.readRate)}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium">{t("metricsFailed")}</CardTitle>
            <XCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold tabular-nums">
              {metrics?.totalFailed.toLocaleString() ?? (isLoading ? "…" : "0")}
            </p>
            {metrics && metrics.totalSkipped > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {t("metricsSkipped")}: {metrics.totalSkipped}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {campaign && metrics && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("detailFunnelTitle")}</CardTitle>
            <CardDescription>{t("detailFunnelDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="text-xs text-muted-foreground">{t("funnelEstimated")}</p>
                <p className="text-2xl font-bold tabular-nums">{campaign.estimatedRecipients ?? 0}</p>
              </div>
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="text-xs text-muted-foreground">{t("metricsSent")}</p>
                <p className="text-2xl font-bold tabular-nums">{metrics.totalSent}</p>
              </div>
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="text-xs text-muted-foreground">{t("metricsDelivered")}</p>
                <p className="text-2xl font-bold tabular-nums">{metrics.totalDelivered}</p>
              </div>
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="text-xs text-muted-foreground">{t("metricsRead")}</p>
                <p className="text-2xl font-bold tabular-nums">{metrics.totalRead}</p>
              </div>
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="text-xs text-muted-foreground">{t("metricsConverted")}</p>
                <p className="text-2xl font-bold tabular-nums">{metrics.conversions}</p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="text-xs text-muted-foreground">{t("reviewEstimatedCost")}</p>
                <p className="text-lg font-semibold tabular-nums">{formatCurrency(estimatedCost ?? 0)}</p>
              </div>
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="text-xs text-muted-foreground">{t("metricsRevenue")}</p>
                <p className="text-lg font-semibold tabular-nums">{formatCurrency(metrics.totalAttributedRevenue)}</p>
              </div>
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="text-xs text-muted-foreground">{t("tableRoiCol")}</p>
                <p className="text-lg font-semibold tabular-nums">{roi === null ? "—" : `${Math.round(roi * 100)}%`}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Segment traceability */}
      {campaign && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("detailSegmentTitle")}</CardTitle>
            <CardDescription>{t("detailSegmentDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="text-xs text-muted-foreground">{t("segmentEstimatedRecipients")}</p>
                <p className="text-2xl font-bold tabular-nums">{campaign.estimatedRecipients ?? 0}</p>
              </div>
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="text-xs text-muted-foreground">{t("segmentTemplate")}</p>
                <p className="text-sm font-medium">{template?.name ?? t("segmentTemplateMissing")}</p>
              </div>
            </div>
            {segmentChips.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("segmentFiltersEmpty")}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {segmentChips.map((chip) => (
                  <Badge key={chip} variant="secondary">{chip}</Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Attribution section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("detailAttributionTitle")}</CardTitle>
          <CardDescription>{t("detailAttributionDesc")}</CardDescription>
        </CardHeader>
        <CardContent>
          {attributions.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("detailAttributionEmpty")}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">{t("metricsRevenue")}</p>
                <p className="text-2xl font-bold tabular-nums">
                  R$ {metrics!.totalAttributedRevenue.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">{t("metricsConverted")}</p>
                <p className="text-2xl font-bold tabular-nums">{metrics!.conversions}</p>
              </div>
              {metrics!.avgDaysToConversion !== null && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">{t("metricsAvgDays")}</p>
                  <p className="text-2xl font-bold tabular-nums">
                    {Math.round(metrics!.avgDaysToConversion)}d
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recipients table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("detailRecipientsTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="text-sm text-muted-foreground p-4">{t("detailLoadingRecipients")}</p>
          ) : recipients.length === 0 ? (
            <p className="text-sm text-muted-foreground p-4">{t("emptyState")}</p>
          ) : (
            <DataTable
              columns={recipientColumns}
              data={recipients}
              defaultDensity="compact"
              getRowId={(recipient) => recipient.id}
              labels={dataTableLabels}
              pageSize={10}
              paginationLabels={paginationLabels}
              persistKey="akuna:campaign-detail:recipients-table:v1"
              showColumnToggle
              showDensityToggle
            />
          )}
        </CardContent>
      </Card>

      {/* Dispatch confirmation */}
      <AlertDialog open={dispatchOpen} onOpenChange={setDispatchOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dispatchConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("dispatchConfirmDesc", {
                count: campaign?.estimatedRecipients ?? 0,
                cost: estimatedCost?.toFixed(2) ?? "0.00",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common:cancel", { defaultValue: "Cancelar" })}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDispatch} disabled={dispatchMutation.isPending}>
              <Send className="h-4 w-4 mr-1" />
              {t("dispatchNow")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
